import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { User, Mail, Lock, Phone, Building, ArrowLeft, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const Signup = () => {
  const navigate = useNavigate();
  const { signup } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    user_type: 'personal',
    company_name: '',
    phone: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    const { confirmPassword, ...signupData } = formData;
    const result = await signup(signupData);

    if (result.success) {
      toast.success('Account created successfully!');
      navigate('/dashboard');
    } else {
      toast.error(result.error);
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen pt-24 pb-16" style={{ background: 'var(--bg-dark)' }}>
      {/* Background Accents */}
      <div className="fixed top-1/4 left-0 w-[400px] h-[400px] bg-[#3B82F6]/10 rounded-full blur-[150px] pointer-events-none"></div>
      <div className="fixed bottom-1/4 right-0 w-[400px] h-[400px] bg-[#8B5CF6]/10 rounded-full blur-[150px] pointer-events-none"></div>

      <div className="relative w-full max-w-2xl mx-auto px-4 sm:px-6">
        <button
          data-testid="back-btn"
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-[#3B82F6] hover:text-white transition-colors duration-300 mb-6"
        >
          <ArrowLeft size={20} />
          <span>Back to Home</span>
        </button>

        <div className="bg-[#12121A]/80 backdrop-blur-xl border border-white/10 rounded-2xl p-6 sm:p-8 md:p-12">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 mb-4">
              <h1 className="text-3xl md:text-4xl font-bold text-white">Create Account</h1>
              <Sparkles size={28} className="text-[#F97316]" />
            </div>
            <p className="text-white/50">Join CLOUD IT to access exclusive services</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* User Type Selection */}
            <div>
              <label className="block text-white/70 mb-3 text-sm font-medium">
                Account Type *
              </label>
              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  data-testid="personal-type"
                  onClick={() => setFormData({ ...formData, user_type: 'personal' })}
                  className={`p-4 rounded-xl transition-all duration-300 ${
                    formData.user_type === 'personal'
                      ? 'border-2 border-[#3B82F6] bg-[#3B82F6]/10'
                      : 'border border-white/10 hover:border-white/20'
                  }`}
                >
                  <User size={24} className={formData.user_type === 'personal' ? 'text-[#3B82F6] mx-auto mb-2' : 'text-white/40 mx-auto mb-2'} />
                  <div className="text-white font-semibold">Personal</div>
                  <div className="text-white/40 text-xs">Individual account</div>
                </button>

                <button
                  type="button"
                  data-testid="business-type"
                  onClick={() => setFormData({ ...formData, user_type: 'business' })}
                  className={`p-4 rounded-xl transition-all duration-300 ${
                    formData.user_type === 'business'
                      ? 'border-2 border-[#8B5CF6] bg-[#8B5CF6]/10'
                      : 'border border-white/10 hover:border-white/20'
                  }`}
                >
                  <Building size={24} className={formData.user_type === 'business' ? 'text-[#8B5CF6] mx-auto mb-2' : 'text-white/40 mx-auto mb-2'} />
                  <div className="text-white font-semibold">Business</div>
                  <div className="text-white/40 text-xs">Company account</div>
                </button>
              </div>
            </div>

            {/* Name */}
            <div>
              <label htmlFor="name" className="flex items-center gap-2 text-white/70 mb-2 text-sm font-medium">
                <User size={16} className="text-[#3B82F6]" />
                Full Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                data-testid="signup-name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full bg-[#0A0A0F] border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#3B82F6] focus:ring-1 focus:ring-[#3B82F6]/50 transition-all duration-300"
                placeholder="John Doe"
              />
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="flex items-center gap-2 text-white/70 mb-2 text-sm font-medium">
                <Mail size={16} className="text-[#8B5CF6]" />
                Email Address *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                data-testid="signup-email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full bg-[#0A0A0F] border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#8B5CF6] focus:ring-1 focus:ring-[#8B5CF6]/50 transition-all duration-300"
                placeholder="john@example.com"
              />
            </div>

            {/* Company Name (if business) */}
            {formData.user_type === 'business' && (
              <div>
                <label htmlFor="company_name" className="flex items-center gap-2 text-white/70 mb-2 text-sm font-medium">
                  <Building size={16} className="text-[#F97316]" />
                  Company Name
                </label>
                <input
                  type="text"
                  id="company_name"
                  name="company_name"
                  data-testid="signup-company"
                  value={formData.company_name}
                  onChange={handleChange}
                  className="w-full bg-[#0A0A0F] border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#F97316] focus:ring-1 focus:ring-[#F97316]/50 transition-all duration-300"
                  placeholder="Your Company Inc."
                />
              </div>
            )}

            {/* Phone */}
            <div>
              <label htmlFor="phone" className="flex items-center gap-2 text-white/70 mb-2 text-sm font-medium">
                <Phone size={16} className="text-[#10B981]" />
                Phone Number
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                data-testid="signup-phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full bg-[#0A0A0F] border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/50 transition-all duration-300"
                placeholder="+1 (555) 000-0000"
              />
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="flex items-center gap-2 text-white/70 mb-2 text-sm font-medium">
                <Lock size={16} className="text-[#3B82F6]" />
                Password *
              </label>
              <input
                type="password"
                id="password"
                name="password"
                data-testid="signup-password"
                value={formData.password}
                onChange={handleChange}
                required
                minLength={6}
                className="w-full bg-[#0A0A0F] border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#3B82F6] focus:ring-1 focus:ring-[#3B82F6]/50 transition-all duration-300"
                placeholder="At least 6 characters"
              />
            </div>

            {/* Confirm Password */}
            <div>
              <label htmlFor="confirmPassword" className="flex items-center gap-2 text-white/70 mb-2 text-sm font-medium">
                <Lock size={16} className="text-[#8B5CF6]" />
                Confirm Password *
              </label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                data-testid="signup-confirm-password"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                className="w-full bg-[#0A0A0F] border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#8B5CF6] focus:ring-1 focus:ring-[#8B5CF6]/50 transition-all duration-300"
                placeholder="Re-enter password"
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              data-testid="signup-submit"
              disabled={loading}
              className="w-full py-4 text-lg font-semibold text-white rounded-xl transition-all duration-300 hover:scale-[1.02] hover:shadow-xl hover:shadow-[#3B82F6]/25 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
              style={{ background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)' }}
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Creating Account...
                </>
              ) : (
                'Create Account'
              )}
            </button>

            {/* Login Link */}
            <p className="text-center text-white/50">
              Already have an account?{' '}
              <Link to="/login" className="text-[#3B82F6] hover:text-[#60A5FA] font-medium transition-colors duration-300">
                Login here
              </Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Signup;
